import React, { memo, MutableRefObject, ReactNode, Suspense, useMemo } from 'react';
import styled from 'styled-components';
import { CardLinkButton } from './Button';
import TriggerPopover from './TriggerPopover';
import DeprecatedTooltip from './Tooltip';
import HintIcon from './HintIcon';
import { useHistory } from 'react-router';
import { FocusTrapZone } from '@fluentui/react';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import ModalContext from '../../components/Context/ModalContext';
import FluentModal from '../modals/FluentModal';

const OverviewTitle = styled.div`
  padding: 16px 16px 8px 16px;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  color: ${(props) => props.theme.colors.inputLabel};
`;

const LazySupplierForm = React.lazy(() => import('../../views/supplier/SupplierForm'));
const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));
const LazyProductForm = React.lazy(() => import('../../views/product/ProductForm'));

type IModalType = 'customer' | 'supplier' | 'product';

const LazyContent = memo(({ type, ...props }: { type: IModalType } & any) => {
  switch (type) {
    case 'supplier':
      return <LazySupplierForm {...props} />;
    case 'product':
      return <LazyProductForm {...props} />;
    default:
      return <LazyCustomerForm {...props} />;
  }
});

type IOverviewCardTitleProps = {
  title: ReactNode;
  linkTabId: string;
  linkTabLabel: string;
  modal?: boolean;
  tabPanelRef?: MutableRefObject<any>;
  entityId?: number;
  type?: IModalType;
  hint?: string;
};

const OverviewCardTitle = memo(
  ({ title, linkTabId, linkTabLabel, modal, tabPanelRef, entityId, type, hint }: IOverviewCardTitleProps) => {
    const history = useHistory();
    const [modalOpen, setModalOpen] = React.useState(false);

    const contentParams = useMemo(() => ({ id: entityId }), [entityId]);

    if (modal) {
      return (
        <>
          <OverviewTitle onClick={() => setModalOpen(true)}>
            {title}
            <CardLinkButton>{linkTabLabel}</CardLinkButton>
          </OverviewTitle>
          <ModalContext.Provider value={{ onClose: () => setModalOpen(false) }}>
            <FluentModal isOpen={modalOpen}>
              <div style={{ height: '90vh', width: '80vw', position: 'relative' }}>
                <Suspense fallback={<ProgressIndicator />}>
                  {modalOpen ? (
                    <FocusTrapZone>
                      <LazyContent type={type} history={history} overrideParams={contentParams} modalClose={true} />{' '}
                    </FocusTrapZone>
                  ) : null}
                </Suspense>
              </div>
            </FluentModal>
          </ModalContext.Provider>
        </>
      );
    }
    let titleElement;
    if (linkTabId) {
      titleElement = (
        <OverviewTitle>
          <div style={{ justifyContent: 'left' }}>
            {title}
            <HintIcon hint={hint} />
          </div>
          {tabPanelRef ? (
            <CardLinkButton onClick={() => tabPanelRef.current.setActiveTabId(linkTabId)}>
              {linkTabLabel}
            </CardLinkButton>
          ) : null}
        </OverviewTitle>
      );
    } else {
      titleElement = <OverviewTitle>{title}</OverviewTitle>;
    }

    if (hint) {
      return (
        <TriggerPopover
          flexBox={false}
          popover={<DeprecatedTooltip value={hint} style={{ top: '0px', left: '1.25rem', marginTop: '38px' }} />}
          styles={{ width: '100%' }}
        >
          {titleElement}
        </TriggerPopover>
      );
    }
    return titleElement;
  }
);

export default OverviewCardTitle;
